/**
 * Time utility functions for consistent UTC time formatting across the application
 * All API records use UTC time, so all UI displays should be in UTC to avoid confusion
 */

/**
 * Format a timestamp as a UTC date and time string
 * @param {string|number|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted UTC date and time string
 */
export const formatUTCDateTime = (timestamp) => {
  if (!timestamp) return 'N/A';

  try {
    let date;

    // Handle different timestamp formats
    if (typeof timestamp === 'number') {
      // Check if it's a Unix timestamp (seconds) or milliseconds
      // Unix timestamps are typically 10 digits, milliseconds are 13 digits
      date = timestamp.toString().length <= 10
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(timestamp);
    }

    // Format as UTC with explicit UTC indicator, removing milliseconds
    return date.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, ' UTC');
  } catch (error) {
    return String(timestamp);
  }
};

/**
 * Format a timestamp as a UTC time string (HH:MM:SS format)
 * @param {string|number|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted UTC time string
 */
export const formatUTCTime = (timestamp) => {
  if (!timestamp) return 'N/A';

  try {
    let date;

    // Handle different timestamp formats
    if (typeof timestamp === 'number') {
      // Check if it's a Unix timestamp (seconds) or milliseconds
      date = timestamp.toString().length <= 10
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(timestamp);
    }

    // Format as UTC time only
    return date.toISOString().substr(11, 8) + ' UTC';
  } catch (error) {
    return String(timestamp);
  }
};

/**
 * Format a timestamp as a UTC date string (YYYY-MM-DD format)
 * @param {string|number|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted UTC date string
 */
export const formatUTCDate = (timestamp) => {
  if (!timestamp) return 'N/A';

  try {
    let date;

    // Handle different timestamp formats
    if (typeof timestamp === 'number') {
      // Check if it's a Unix timestamp (seconds) or milliseconds
      date = timestamp.toString().length <= 10
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(timestamp);
    }

    // Format as UTC date only
    return date.toISOString().substr(0, 10);
  } catch (error) {
    return String(timestamp);
  }
};

/**
 * Format a timestamp for chart display (short time format)
 * @param {string|number|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted time string for charts
 */
export const formatChartTime = (timestamp) => {
  if (!timestamp) return '';

  try {
    let date;

    // Handle different timestamp formats
    if (typeof timestamp === 'number') {
      date = timestamp.toString().length <= 10
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Format as short UTC time for charts (HH:MM:SS) to handle 10-second intervals
    return date.toISOString().substr(11, 8);
  } catch (error) {
    return '';
  }
};

/**
 * Get the current UTC timestamp as ISO string
 * @returns {string} - Current UTC timestamp in ISO format
 */
export const getCurrentUTCTimestamp = () => {
  return new Date().toISOString();
};

/**
 * Check if a timestamp is valid
 * @param {string|number|Date} timestamp - The timestamp to validate
 * @returns {boolean} - True if timestamp is valid
 */
export const isValidTimestamp = (timestamp) => {
  if (!timestamp) return false;

  try {
    let date;

    if (typeof timestamp === 'number') {
      date = timestamp.toString().length <= 10
        ? new Date(timestamp * 1000)
        : new Date(timestamp);
    } else {
      date = new Date(timestamp);
    }

    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};


/**
 * Format a time duration in milliseconds into a human-readable string
 * @param {number} durationMs - Duration in milliseconds
 * @param {number} upperLimitMs - Upper limit for milliseconds display (default 1000)
 * @param {number} precisionSec - Number of decimal places for seconds (default 3)
 * @returns {string} - Formatted time duration
 */
export const formatTimeDurationMs = (durationMs, upperLimitMs = 1000, precisionSec = 3) => {
  if (durationMs == null || isNaN(durationMs)) return 'N/A';

  // Handle durations less than 1 second
  if (durationMs < 1000) {
    return `${Math.floor(durationMs)} ms`;
  }

  // Handle durations less than the upper limit (e.g., 10 seconds)
  if (durationMs < upperLimitMs) {
    return `${(durationMs / 1000).toFixed(precisionSec)} s`;
  }

  // For durations longer than the upper limit, format into days, hours, minutes, seconds
  const seconds = Math.floor((durationMs / 1000) % 60);
  const minutes = Math.floor((durationMs / (1000 * 60)) % 60);
  const hours = Math.floor((durationMs / (1000 * 60 * 60)) % 24);
  const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));

  let formattedDuration = '';
  if (days > 0) formattedDuration += `${days}d `;
  if (hours > 0 || days > 0) formattedDuration += `${hours}h `;
  if (minutes > 0 || hours > 0 || days > 0) formattedDuration += `${minutes}m `;
  formattedDuration += `${seconds}s`;

  return formattedDuration.trim();
};